import { Link } from "react-router-dom";
import { getBlogUrl } from '@/data/blogSlugs';
import { HOMEPAGE_SERVICES } from '@/data/homepageServices';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel"
import Navigation from "@/components/Navigation";
import WinterFocus from '@/components/WinterFocus';
import Footer from "@/components/Footer";
import PopularSearchLinks from "@/components/PopularSearchLinks";
import EnglishServicesLinks from '@/components/en/EnglishServicesLinks';
import SEOHead from "@/components/SEO/SEOHead";
import VisualBreadcrumbs from "@/components/SEO/VisualBreadcrumbs";
import LocalBusinessSchema from "@/components/SEO/LocalBusinessSchema";
import FAQSchema from "@/components/SEO/FAQSchema";
import HowToSchema from "@/components/SEO/HowToSchema";
import BlogSchema from "@/components/SEO/BlogSchema";
import ElfsightReviews from "@/components/ElfsightReviews";
import AwtPillarLink from "@/components/SEO/AwtPillarLink";
import {
  Heart, 
  Activity, 
  Award, 
  ArrowRight, 
  Phone,
  MapPin,
  CheckCircle,
  Sparkles,
  Stethoscope,
  Droplets,
  Zap
} from "lucide-react";
const celluliteRemovalImage = "/images/services/cellulite-removal.webp";
const lymphaticDrainageImage = "/images/services/limfna-drenaza.webp";
const pressotherapyImage = "/images/services/presoterapija.webp";
const teamPhoto = "/images/dijagnostika-novi-beograd.jpg";
const heroPhoto = "/images/prvi-dolazak-physiowrld.webp";

const Index = () => {
  const { t, i18n } = useTranslation();
  
  const allBlogs = (t('homepage.blogs', { returnObjects: true }) as any[]) || [];
  
  const serviceIcons: Record<string, typeof Activity> = {
    kiropraktika: Heart,
    'limfna-drenaza': Droplets,
    shockwave: Zap,
    'fizikalna-terapija': Award,
  };

  const services = HOMEPAGE_SERVICES.map((service) => ({
    href: service.href,
    img: service.img,
    icon: serviceIcons[service.id] ?? Activity,
    title: t(service.titleKey),
    description: t(service.descriptionKey),
    price: service.priceFrom ? `${t('homepage.from')} ${service.price}` : service.price,
  }));

  const faqs = (t('homepage.faqs', { returnObjects: true }) as any[]) || [];

  // HowTo Schema steps for first visit
  const howToSteps = [
    {
      name: "Zakazivanje termina",
      text: "Kontakt putem telefona ili forme na sajtu i izbor termina.",
      url: "https://physiowrld.rs/kontakt"
    },
    { name: "Prijem i anamneza", text: "Razgovor o tegobama, ciljevima i prethodnim nalazima." },
    { name: "Funkcionalna procena", text: "Procena posture, obima pokreta, snage i specifičnih testova zahvaćenog segmenta." },
    { name: "Plan terapije", text: "Izrada personalizovanog plana: kineziterapija, manuelne tehnike, elektro/ultrazvuk, masaže." },
    { name: "Prvi tretman", text: "Početak terapije uz edukaciju i kućni program vežbi." },
    { name: "Kontrola napretka", text: "Praćenje rezultata i prilagođavanje plana na narednim dolascima." }
  ];

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="Fizikalna terapija Beograd – PHYSIOWRLD Novi Beograd"
        description="Fizikalna terapija u Beogradu — PHYSIOWRLD centar na Novom Beogradu (Blok 64). Rehabilitacija, kiropraktika, kineziterapija, shockwave i terapeutska masaža. Zakažite termin."
        canonical="https://physiowrld.rs/"
        ogImage="https://physiowrld.rs/images/physiowrld-logo.webp"
      />
      <LocalBusinessSchema />
      <FAQSchema faqs={faqs} />
      <HowToSchema 
        name="Prvi dolazak u PHYSIOWRLD – fizikalna terapija Novi Beograd"
        description="Kako izgleda prvi dolazak: anamneza, funkcionalna procena, izrada individualnog terapijskog plana i početak rehabilitacije."
        steps={howToSteps}
        language="sr-RS"
      />
      <BlogSchema />
      <Navigation />
      
      <main>
      {/* Hero Section */}
      <section className="relative pt-20 pb-12 lg:pt-28 lg:pb-24 overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div>
            <p className="mb-3 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs sm:text-sm font-medium text-primary">
              <CheckCircle className="h-4 w-4 shrink-0" />
              {t('homepage.servicesFreeNote')}
            </p>
            <h1 className="text-[1.7rem] sm:text-4xl lg:text-5xl font-extrabold mb-3 leading-[1.15] tracking-tight">
              {t('homepage.heroTitle')}
            </h1>
            <p className="mx-auto max-w-2xl text-sm sm:text-base lg:text-lg text-muted-foreground mb-5 leading-relaxed">
              {t('homepage.heroSubtitle')}
            </p>
            <div className="mb-5 glass-card rounded-xl px-4 py-3 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs sm:text-sm">
              <span className="inline-flex items-center gap-2">
                <Award className="h-4 w-4 shrink-0 text-primary" />
                <span className="font-semibold"><span className="gradient-text">1000+</span> {t('homepage.rehabilitatedPatients')}</span>
              </span>
              <span className="inline-flex items-center gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-primary" />
                <span className="font-semibold">{t('homepage.newBelgrade')}, Omladinskih brigada 96E/4</span>
              </span>
              <a href="tel:+381605037477" className="inline-flex items-center gap-2 hover:underline underline-offset-4">
                <Phone className="h-4 w-4 shrink-0 text-primary" />
                <span className="font-semibold text-primary">+381 60 503 7477</span>
              </a>
            </div>
            <div className="mx-auto grid max-w-md grid-cols-2 gap-3 mb-3">
              <Button variant="hero" size="lg" asChild>
                <Link to="/contact">
                  {t('homepage.bookExam')}
                  <ArrowRight className="ml-1 h-4 w-4 shrink-0" />
                </Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <a href="tel:+381605037477">
                  <Phone className="mr-1 h-4 w-4 shrink-0" />
                  {t('homepage.callUs')}
                </a>
              </Button>
            </div>
            <Link to="/sta-me-boli" className="inline-flex items-center gap-2 text-sm font-medium text-primary hover:underline underline-offset-4">
              <Stethoscope className="h-4 w-4" />
              {t('nav.symptomAdvisor')}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          <div className="mt-20 sm:mt-8 overflow-hidden rounded-2xl border border-border/50 shadow-elegant">
            <img
              src={heroPhoto}
              sizes="(min-width: 896px) 896px, 100vw"
              alt="Fizioterapeut pregleda pacijenta u PHYSIOWRLD centru za fizikalnu terapiju na Novom Beogradu"
              className="aspect-[16/9] w-full object-cover object-center"
              width="1600"
              height="900"
              loading="eager"
              decoding="async"
            />
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 cv-auto">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4">
              {t('homepage.servicesTitle')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('homepage.servicesSubtitle')}
            </p>
            <p className="mt-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary">
              <CheckCircle className="h-4 w-4 shrink-0" />
              {t('homepage.servicesFreeNote')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Link
                  key={index}
                  to={service.href}
                  className="group glass-card flex items-start gap-4 rounded-2xl p-4 transition-all duration-300 hover:border-primary/40 hover:shadow-elegant"
                >
                  <div className="relative h-20 w-28 shrink-0 overflow-hidden rounded-xl">
                    <img
                      src={service.img}
                      alt={`${service.title} - PHYSIOWRLD Novi Beograd`}
                      width={224}
                      height={160}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="flex items-center gap-2 text-lg font-semibold transition-colors group-hover:text-primary">
                      <Icon className="h-4 w-4 shrink-0 text-primary" />
                      <span>{service.title}</span>
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {service.description}
                    </p>
                    <div className="mt-2 flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-primary">{service.price}</span>
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-primary">
                        {t('homepage.servicesOpen')}
                        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            <Button variant="medical" size="lg" asChild>
              <Link to="/services">
                {t('services.viewAll')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Body Care Services */}
      <section className="py-20 cv-auto bg-gradient-to-br from-emerald-900/20 via-teal-900/10 to-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-64 h-64 bg-teal-500/10 rounded-full blur-2xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-bold px-4 py-2 rounded-full mb-6 animate-pulse">
              <Sparkles className="h-4 w-4" />
              {t('homepage.newInOffer')}
              <Sparkles className="h-4 w-4" />
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('homepage.bodyCareTitle').split(' ').map((word, i, arr) => 
                i >= arr.length - 2 ? <span key={i} className="bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">{word} </span> : word + ' '
              )}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('homepage.bodyCareSubtitle')}
            </p>
          </div>
          
          <Carousel className="max-w-4xl mx-auto" opts={{ loop: true }}>
            <CarouselContent>
              {[
                { href: "/tretman-celulita-novi-beograd", img: celluliteRemovalImage, title: t('homepage.celluliteTitle'), desc: t('homepage.celluliteDesc'), alt: "Anticelulit tretman i nehirurška lipoliza Shockwave AWT - PHYSIOWRLD", price: "3.500" },
                { href: "/limfna-drenaza-novi-beograd", img: lymphaticDrainageImage, title: t('homepage.lymphaticDrainageTitle'), desc: t('homepage.lymphaticDrainageDesc'), alt: "Limfna drenaža - PHYSIOWRLD", price: "3.500 / 6.000", priceNote: "30 / 60 min" },
                { href: "/services/pressotherapy", img: pressotherapyImage, title: t('homepage.pressotherapyTitle'), desc: t('homepage.pressotherapyDesc'), alt: "Presoterapija za noge - PHYSIOWRLD", price: "2.000", priceNote: null },
              ].map((item, i) => (
                <CarouselItem key={i} className="h-full">
                  <Link to={item.href} className="group block h-full">
                    <Card className="glass-card overflow-hidden hover:scale-[1.02] transition-all duration-300 border-emerald-500/20 hover:border-emerald-500/50 h-[520px] md:h-[420px]">
                      <CardContent className="p-0 h-full">
                        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch h-full">
                          <div className="relative h-64 md:h-full overflow-hidden">
                            <div className="absolute top-3 right-3 z-10 bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                              {t('homepage.new')}
                            </div>
                            <img src={item.img} alt={item.alt} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" loading="lazy" />
                            <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-background via-transparent to-transparent"></div>
                          </div>
                          <div className="p-6 md:p-8 flex flex-col justify-center h-full">
                            <h3 className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-emerald-400 transition-colors">{item.title}</h3>
                            <p className="text-muted-foreground text-base mb-6 leading-relaxed">{item.desc}</p>
                            <div className="flex items-center justify-between pt-4 border-t border-emerald-500/20 mt-auto">
                              <div>
                                <span className="text-emerald-400 font-bold text-lg">{t('homepage.from')} {item.price} RSD</span>
                                {item.priceNote && (
                                  <span className="text-xs text-amber-400 mt-1 block">{item.priceNote}</span>
                                )}
                              </div>
                              <ArrowRight className="h-6 w-6 text-emerald-400 group-hover:translate-x-2 transition-transform" />
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2 md:-left-12" />
            <CarouselNext className="right-2 md:-right-12" />
          </Carousel>

          {/* Benefits strip */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="text-2xl font-bold text-emerald-400">6-12</div>
              <div className="text-sm text-muted-foreground">{t('homepage.treatmentsForResults')}</div>
            </div>
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="text-2xl font-bold text-emerald-400">48h</div>
              <div className="text-sm text-muted-foreground">{t('homepage.pauseBetween')}</div>
            </div>
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="text-2xl font-bold text-emerald-400">0</div>
              <div className="text-sm text-muted-foreground">{t('homepage.recoveryDays')}</div>
            </div>
            <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
              <div className="text-2xl font-bold text-emerald-400">100%</div>
              <div className="text-sm text-muted-foreground">{t('homepage.nonInvasive')}</div>
            </div>
          </div>

          <div className="text-center mt-12 space-y-4">
            <Button variant="hero" size="xl" asChild className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600">
              <Link to="/contact">
                {t('homepage.bookConsultation')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <div>
              <Link to="/tretman-celulita-novi-beograd" className="text-emerald-400 hover:underline font-medium text-base">
                {i18n.language === 'en'
                  ? 'AWT treatment and anti-cellulite treatments — complete guide'
                  : i18n.language === 'ru'
                  ? 'AWT процедура и антицеллюлитные процедуры — полный гид'
                  : 'AWT tretman i anticelulit tretmani — kompletan vodič'} →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="py-20 cv-auto bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl lg:text-5xl font-bold mb-6">
                {t('homepage.whyChoose.title')}
              </h2>
              <h3 className="text-2xl font-semibold mb-4 gradient-text">
                {t('homepage.whyChoose.subtitle')}
              </h3>
              <p className="text-lg text-muted-foreground mb-8">
                {t('homepage.whyChoose.description')} {t('homepage.aboutLinkText')}
              </p>
              
              <div className="space-y-4 mb-8">
                {(t('homepage.whyChoose.benefits', { returnObjects: true }) as string[]).map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary" />
                    <span className="text-muted-foreground">{benefit}</span>
                  </div>
                ))}
              </div>
              
              <Button variant="cta" size="lg" asChild>
                <Link to="/contact">{t('homepage.bookFirstExam')}</Link>
              </Button>
            </div>
            
            <div className="relative">
              <img 
                src={teamPhoto} 
                alt="Fizioterapeut obavlja pregled i ultrazvučnu dijagnostiku u PHYSIOWRLD centru na Novom Beogradu"
                className="rounded-2xl shadow-elegant w-full object-cover aspect-video object-center"
                loading="lazy"
                decoding="async"
                width="800"
                height="450"
              />
              <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary/20 rounded-full blur-2xl"></div>
              <div className="absolute -top-6 -right-6 w-16 h-16 bg-primary-glow/30 rounded-full blur-xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Pillar Page Link Section */}
      <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4">
            <Link to="/fizikalna-terapija-beograd" className="hover:text-primary transition-colors">
              {t('homepage.pillarLinkTitle')}
            </Link>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
            {t('homepage.pillarLinkSubtitle')}
          </p>
          <Button variant="medical" size="lg" asChild>
            <Link to="/fizikalna-terapija-beograd">
              {t('homepage.pillarLinkCta')}
              <ArrowRight className="ml-2 h-5 w-5" />
            </Link>
          </Button>
        </div>
      </section>

      {/* Blog Preview Section */}
      <section id="blog-section" className="py-14 bg-card/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('homepage.blogTitle')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              {t('homepage.blogSubtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {allBlogs.map((blog: any, idx: number) => (
              <article key={idx} className="blog-card">
                <h3 className="text-lg font-semibold mb-2 leading-tight">
                  <Link to={getBlogUrl(idx + 1)} className="hover:text-primary transition-colors">
                    {blog.title}
                  </Link>
                </h3>
                <p className="text-muted-foreground text-sm mb-0 line-clamp-2">
                  {blog.description}
                </p>
                <Link 
                  to={getBlogUrl(idx + 1)} 
                  aria-label={`${t('homepage.readMore')}: ${blog.title}`}
                  className="more text-primary hover:underline text-sm font-medium inline-flex items-center gap-1 mt-auto"
                >
                  {t('homepage.readMore')} <span className="sr-only">: {blog.title}</span><ArrowRight className="h-3 w-3" />
                </Link>
              </article>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button variant="medical" size="lg" asChild>
              <Link to="/blog">
                {t('homepage.viewAllArticles')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>
            <p className="mt-6 text-sm text-muted-foreground">
              <Link
                to="/fizikalna-terapija-beograd"
                className="text-primary hover:underline font-medium"
              >
                {i18n.language === 'en'
                  ? 'Physical therapy Belgrade — complete guide'
                  : i18n.language === 'ru'
                  ? 'Физиотерапия Белград — полный гид'
                  : 'Fizikalna terapija Beograd — kompletan vodič'}
              </Link>
            </p>
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <WinterFocus />
      <section id="reviews-section" className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('homepage.reviewsTitle')}
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto mb-6">
              {t('homepage.reviewsSubtitle')}
            </p>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {t('homepage.reviewsDescription')}
            </p>
          </div>

          <div className="reviews-widget-container">
            <ElfsightReviews />
          </div>

          <div className="text-center mt-12">
            <Button variant="hero" size="lg" asChild>
              <a 
                href="https://www.google.com/maps/place/PHYSIOWRLD/@44.810219,20.403981" 
                target="_blank" 
                rel="noopener noreferrer"
              >
                {t('homepage.viewAllReviews')}
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 cv-auto bg-card/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              {t('homepage.faqTitle')}
            </h2>
            <p className="text-xl text-muted-foreground">
              {t('homepage.faqSubtitle')}
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq: any, index: number) => (
              <Card key={index} className="glass-card">
                <CardContent className="p-0">
                  <details className="group">
                    <summary className="flex justify-between items-center cursor-pointer p-6 font-semibold text-lg">
                      {faq.question}
                      <ArrowRight className="h-5 w-5 text-primary group-open:rotate-90 transition-transform" />
                    </summary>
                    <div className="px-6 pb-6 text-muted-foreground">
                      {faq.answer}
                    </div>
                  </details>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact/Map Section */}
      <section className="py-20 cv-auto bg-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold mb-6">
                {t('homepage.contactTitle')}
              </h2>
              <p className="text-lg text-muted-foreground mb-8">
                {t('homepage.contactSubtitle')}
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <MapPin className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{t('contact.location')}</div>
                    <div className="text-muted-foreground">{t('contact.address')}</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{t('contact.phone')}</div>
                    <div className="text-muted-foreground">{t('contact.phoneNumber')}</div>
                  </div>
                </div>
              </div>
              
              <div className="mt-8">
                <Button variant="hero" size="lg" asChild>
                  <Link to="/contact">{t('contact.getDirections')}</Link>
                </Button>
              </div>
            </div>
            
            <div className="rounded-2xl overflow-hidden shadow-elegant h-[400px] lg:h-full min-h-[400px]">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2830.0394887956847!2d20.397469776556447!3d44.81877217107158!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x475a7013e0c24b05%3A0x8f3c9e5c7b8d9a6e!2sOmladinskih%20brigada%2096E%2C%20Beograd%2C%20Serbia!5e0!3m2!1sen!2s!4v1234567890123!5m2!1sen!2s"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="PHYSIOWRLD Location"
              />
            </div>
          </div>
        </div>
      </section>
      <PopularSearchLinks />
      <EnglishServicesLinks />
      </main>

      <Footer />

    </div>
  );
};

export default Index;
